import { useCallback, useEffect, useMemo, useReducer, useRef, useState, type ReactNode } from 'react';
import type { Ecran } from '../../api/contrat.js';
import { creerClient, ErreurApi } from '../../api/client.js';
import { modeMocks } from '../../app/entree.js';
import { creerFetchMocke, sessionDepuisAdresse } from '../../mocks/fetch-mocke.js';
import { FenetreBeta, questionsDues, repondreBeta } from '../../app/beta.js';
import type { QuestionBeta } from '../../api/contrat.js';
import { COULEURS, POLICE_EQUIPE } from '../../app/styles.js';
import { creerActions, type ActionsDossier, type ReglageAppris } from './actions.js';
import { CHRONO_INITIAL, ouvrir, periode, totalMs, type EtatChrono } from './chronometre.js';
import { EDITEUR_INITIAL, reducerEditeur } from './editeur.js';
import { EditeurEtapes } from './EditeurEtapes.js';
import { etatInitial, idDossierDepuisAdresse, reducerEcran } from './ecran.js';
import { deplacer, resumeParcours } from './parcours.js';
import { REFERENTIEL_L53 } from './referentiel.js';
import { ListeDossiers } from './ListeDossiers.js';
import { creerSourceDossiers } from './source-liste.js';
import { VueEnsemble, type EtatJournal } from './VueEnsemble.js';
import { SousVueDossier } from './SousVueDossier.js';
import { vueDepuisAdresse } from './sections.js';

/**
 * Conteneur de D03 : charge le dossier désigné par `?dossier=`, tient l'éditeur d'étapes et le chronomètre RG-24.
 * Le client de l'API est construit ici tant que la coquille n'en fournit pas aux écrans (RFC-L53-001) : avec
 * `?role=` (parcours de démonstration de la coquille), les réponses viennent des exemples du contrat.
 */
const recherche = (): string => globalThis.location?.search ?? '';

function clientDeLEcran() {
  // S1 du 27/10 : `?role=` ne vaut qu'en développement avec les mocks (modeMocks), jamais en production.
  const demo = modeMocks(import.meta.env) && sessionDepuisAdresse(recherche()) !== null;
  return creerClient({ fetch: demo ? creerFetchMocke({ session: sessionDepuisAdresse(recherche()) }) : globalThis.fetch.bind(globalThis) });
}

const aujourdhui = (): string => new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Guadeloupe' });
/** RG-24 : ce que l'écran dit quand le temps passé ne peut pas être lu ou enregistré (jamais un faux succès). */
const MESSAGES_TEMPS = {
  lecture: 'Le temps passé enregistré n’a pas pu être lu : le total affiché ne compte que cette visite. Cabinet MAAT',
  envoi: 'Une période de temps passé n’a pas pu être enregistrée : corrigez le total si besoin. Cabinet MAAT',
} as const;

const messageDe = (e: unknown): string =>
  e instanceof ErreurApi ? e.messageUtilisateur : e instanceof Error ? e.message : 'Une erreur est survenue. Réessayez.';

function Cadre({ ecran, children }: { ecran: Ecran; children: ReactNode }) {
  return (
    <section
      data-ecran={ecran.id}
      data-ecran-dossier={ecran.id}
      style={{ fontFamily: POLICE_EQUIPE, display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <h1 style={{ color: COULEURS.marine, fontSize: 28, margin: 0 }}>{ecran.titre}</h1>
      {children}
    </section>
  );
}

/** Largeur de la fenêtre, remesurée à chaque redimensionnement : la coquille ne la transmet pas aux écrans. */
function useLargeur(): number {
  const lire = () => globalThis.innerWidth ?? 1280;
  const [largeur, setLargeur] = useState(lire);
  useEffect(() => {
    const mesurer = () => setLargeur(lire());
    mesurer();
    globalThis.addEventListener?.('resize', mesurer);
    return () => globalThis.removeEventListener?.('resize', mesurer);
  }, []);
  return largeur;
}

/**
 * D03 : sans `?dossier=`, la liste des dossiers (avec « Nouveau dossier ») ; avec, le dossier ouvert. Le dossier est relu à
 * chaque rendu et sert de clé : ouvrir un autre dossier, ou celui qui vient d'être créé, repart d'un état neuf.
 */
export function EcranDossier({ ecran }: { ecran: Ecran }) {
  const idDossier = idDossierDepuisAdresse(recherche());
  if (idDossier === null) return <ListeDesDossiers />;
  // PARITE-4 : sous-vues portées par D03 (`?vue=`), comme les onglets du dossier de la démo ; inconnue : vue d'ensemble.
  const vue = vueDepuisAdresse(recherche());
  if (vue !== 'ensemble')
    return <SousVueDossier key={`${idDossier}:${vue}`} idDossier={idDossier} vue={vue} fabriqueClient={clientDeLEcran} />;
  return <DossierOuvert key={idDossier} ecran={ecran} idDossier={idDossier} />;
}

function ListeDesDossiers() {
  const source = useMemo(() => creerSourceDossiers(clientDeLEcran()), []);
  return <ListeDossiers key={new URLSearchParams(recherche()).get('nouveau') ?? ''} source={source} />;
}

function DossierOuvert({ ecran, idDossier }: { ecran: Ecran; idDossier: string }) {
  const [etat, envoyer] = useReducer(reducerEcran, idDossier, etatInitial);
  const [editeur, agirEditeur] = useReducer(reducerEditeur, EDITEUR_INITIAL);
  const [chrono, setChrono] = useState(CHRONO_INITIAL);
  // RG-24 (RFC-RG24-001) : total enregistré par l'API, plus les périodes en cours d'envoi ; la période en cours vit sur l'appareil.
  const chronoCourant = useRef<EtatChrono>(CHRONO_INITIAL);
  const [tempsServeur, setTempsServeur] = useState(0);
  const [tempsEnvoiMs, setTempsEnvoiMs] = useState(0);
  const [erreurTemps, setErreurTemps] = useState<string | null>(null);
  const largeur = useLargeur();
  const [journal, setJournal] = useState<EtatJournal>({ etat: 'chargement' });
  const [courrierAnnonce, setCourrierAnnonce] = useState<ReglageAppris | null | undefined>(undefined);
  const [lectriceDirigeante, setLectriceDirigeante] = useState(false);
  // Étape regardée dans la frise (null : l'étape en cours), affichage progressif de la vue d'ensemble.
  const [etapeChoisie, setEtapeChoisie] = useState<string | null>(null);
  const actions = useRef<ActionsDossier | null>(null);

  const charger = useCallback(async () => {
    actions.current ??= creerActions(clientDeLEcran(), idDossier);
    envoyer({ type: 'charger' });
    try {
      envoyer({ type: 'charge', dossier: await actions.current.charger() });
    } catch (e) {
      envoyer({ type: 'echec', message: messageDe(e) });
    }
  }, [idDossier]);

  // Le journal est lu à part : sa panne n'empêche jamais d'afficher le dossier.
  const chargerJournal = useCallback(async () => {
    actions.current ??= creerActions(clientDeLEcran(), idDossier);
    setJournal({ etat: 'chargement' });
    try {
      setJournal({ etat: 'pret', entrees: await actions.current.journal() });
    } catch (e) {
      setJournal({ etat: 'erreur', message: messageDe(e) });
    }
  }, [idDossier]);

  // Le rôle de la lectrice n'est qu'un confort de libellé : sa panne ne bloque rien.
  const chargerLectrice = useCallback(async () => {
    actions.current ??= creerActions(clientDeLEcran(), idDossier);
    try {
      setLectriceDirigeante((await actions.current.rolesLectrice()).includes('dirigeante'));
    } catch {
      setLectriceDirigeante(false);
    }
  }, [idDossier]);

  // Réglage BETA du courrier d'annonce (lot 4, point 7) : lu à part ; illisible (droit, panne), le bloc n'est pas affiché.
  const chargerCourrier = useCallback(async () => {
    if (idDossier === null) return;
    actions.current ??= creerActions(clientDeLEcran(), idDossier);
    try {
      setCourrierAnnonce(await actions.current.courrierAnnonce());
    } catch {
      setCourrierAnnonce(undefined);
    }
  }, [idDossier]);

  useEffect(() => {
    void charger();
    void chargerJournal();
    void chargerLectrice();
    void chargerCourrier();
  }, [charger, chargerJournal, chargerLectrice, chargerCourrier]);

  const majChrono = useCallback((etat: EtatChrono) => {
    chronoCourant.current = etat;
    setChrono(etat);
  }, []);

  /** Envoie une période close à l'API ; le total affiché la compte pendant l'envoi, puis reprend le total enregistré. */
  const envoyerPeriode = useCallback(
    async (p: { debut: number; fin: number } | null): Promise<void> => {
      if (!p) return;
      const duree = p.fin - p.debut;
      setTempsEnvoiMs((x) => x + duree);
      try {
        const v = await clientDeLEcran().appeler<{ totalMs: number }>('temps.enregistrer', {
          parametres: { dossierId: idDossier },
          corps: { startedAt: new Date(p.debut).toISOString(), endedAt: new Date(p.fin).toISOString() },
        });
        setTempsServeur(v.totalMs);
        setErreurTemps(null);
      } catch {
        setErreurTemps(MESSAGES_TEMPS.envoi);
      } finally {
        setTempsEnvoiMs((x) => x - duree);
      }
    },
    [idDossier],
  );

  // RG-24 : le chronomètre démarre à l'entrée dans le dossier, suit l'activité et s'arrête à la sortie. Chaque période close
  // (sortie, inactivité, fermeture de l'onglet) est enregistrée par l'API ; le total vient du serveur.
  useEffect(() => {
    const inactivite = REFERENTIEL_L53.inactiviteChronoMs;
    void clientDeLEcran()
      .appeler<{ totalMs: number }>('temps.dossier', { parametres: { dossierId: idDossier } })
      .then((v) => setTempsServeur(v.totalMs))
      .catch(() => setErreurTemps(MESSAGES_TEMPS.lecture));
    majChrono(ouvrir(CHRONO_INITIAL, Date.now()));
    const marquer = () => {
      const maintenant = Date.now();
      const c = chronoCourant.current;
      if (c.ouvertA === null) return;
      if (maintenant - (c.derniereActivite ?? c.ouvertA) > inactivite) {
        void envoyerPeriode(periode(c, maintenant, inactivite));
        majChrono(ouvrir(CHRONO_INITIAL, maintenant));
      } else majChrono({ ...c, derniereActivite: maintenant });
    };
    // Fermeture de l'onglet : la période part en `keepalive`, la seule requête qu'un navigateur laisse finir après la page.
    const quitter = () => {
      const p = periode(chronoCourant.current, Date.now(), inactivite);
      majChrono(ouvrir(CHRONO_INITIAL, Date.now()));
      if (!p) return;
      void globalThis
        .fetch?.(`/api/v1/dossiers/${idDossier}/temps`, {
          method: 'POST',
          keepalive: true,
          credentials: 'include',
          headers: { 'content-type': 'application/json', 'Idempotency-Key': globalThis.crypto.randomUUID() },
          body: JSON.stringify({ startedAt: new Date(p.debut).toISOString(), endedAt: new Date(p.fin).toISOString() }),
        })
        .catch(() => undefined);
    };
    const evenements = ['pointerdown', 'keydown', 'scroll'] as const;
    for (const nom of evenements) globalThis.addEventListener?.(nom, marquer, { passive: true });
    globalThis.addEventListener?.('pagehide', quitter);
    const tic = globalThis.setInterval(() => setChrono((c) => ({ ...c })), 30_000);
    return () => {
      for (const nom of evenements) globalThis.removeEventListener?.(nom, marquer);
      globalThis.removeEventListener?.('pagehide', quitter);
      globalThis.clearInterval(tic);
      void envoyerPeriode(periode(chronoCourant.current, Date.now(), inactivite));
      majChrono(CHRONO_INITIAL);
    };
  }, [idDossier, envoyerPeriode, majChrono]);

  /** Correction : la période en cours part d'abord, puis le nouveau total et son motif ; l'écran affiche le total enregistré. */
  const corrigerTemps = useCallback(
    async (nouveauTotalMs: number, motif: string): Promise<void> => {
      const maintenant = Date.now();
      const p = periode(chronoCourant.current, maintenant, REFERENTIEL_L53.inactiviteChronoMs);
      majChrono(ouvrir(CHRONO_INITIAL, maintenant));
      await envoyerPeriode(p);
      try {
        const v = await clientDeLEcran().appeler<{ totalMs: number }>('temps.corriger', {
          parametres: { dossierId: idDossier },
          corps: { totalMs: nouveauTotalMs, reason: motif },
        });
        setTempsServeur(v.totalMs);
        setErreurTemps(null);
      } catch (e) {
        setErreurTemps(messageDe(e));
      }
    },
    [idDossier, envoyerPeriode, majChrono],
  );

  const ecrire = useCallback(
    async (operation: (a: ActionsDossier) => Promise<void>) => {
      if (!actions.current) return;
      agirEditeur({ type: 'occupe' });
      try {
        await operation(actions.current);
        agirEditeur({ type: 'termine' });
        await charger();
      } catch (e) {
        agirEditeur({ type: 'echec', message: messageDe(e) });
      }
    },
    [charger],
  );

  // Question BETA « accès extérieur après clôture » (BETA-05, déclencheur dossier.closed) : posée quand le dossier est clos,
  // en pop-up ; la coquille n'interroge que l'identifiant d'écran, elle ne serait sinon jamais posée (lot 4, point 8 a).
  const [questionCloture, setQuestionCloture] = useState<QuestionBeta | null>(null);
  const statutDossier = etat.type === 'pret' ? etat.dossier.status : null;
  useEffect(() => {
    if (statutDossier !== 'clos') return;
    let actif = true;
    void questionsDues(clientDeLEcran(), 'dossier.closed').then((qs) => {
      if (actif) setQuestionCloture(qs[0] ?? null);
    });
    return () => {
      actif = false;
    };
  }, [statutDossier]);

  // Inatteignable (un dossier désigné donne « chargement » dès le départ) ; garde le reducer exhaustif sans cul-de-sac.
  if (etat.type === 'sans-dossier') return <ListeDesDossiers />;
  if (etat.type === 'chargement') {
    return (
      <Cadre ecran={ecran}>
        <p role="status">Chargement du dossier…</p>
      </Cadre>
    );
  }
  if (etat.type === 'erreur') {
    return (
      <Cadre ecran={ecran}>
        <p role="alert">{etat.message}</p>
        <button type="button" style={{ minHeight: 44 }} onClick={() => void charger()}>
          Réessayer
        </button>
      </Cadre>
    );
  }

  const dossier = etat.dossier;
  const ids = resumeParcours(dossier.steps).map((e) => e.id);
  // ARB-DOSSIER-48/49 : aux bornes, l'ordre ne change pas, donc aucune écriture (ni ligne de journal) ne part.
  const deplacerEtape = (id: string, sens: -1 | 1) => {
    const ordre = deplacer(ids, id, sens);
    if (ordre.every((x, i) => x === ids[i])) return;
    void ecrire((a) => a.reordonner(ordre));
  };
  const maintenant = Date.now();
  return (
    <>
      {questionCloture ? (
        <FenetreBeta
          question={questionCloture}
          onRepondre={(option) => {
            const q = questionCloture;
            setQuestionCloture(null);
            void repondreBeta(clientDeLEcran(), q, option, 'D03').catch(() => undefined);
          }}
          onPlusTard={() => setQuestionCloture(null)}
        />
      ) : null}
      <VueEnsemble
        dossier={dossier}
        aujourdhui={aujourdhui()}
        largeur={largeur}
        chrono={{
          totalMs: tempsServeur + tempsEnvoiMs + totalMs(chrono, maintenant, REFERENTIEL_L53.inactiviteChronoMs),
          enCours: chrono.ouvertA !== null,
          erreur: erreurTemps,
        }}
        onCorrigerTemps={() => {
          const actuel = tempsServeur + tempsEnvoiMs + totalMs(chrono, maintenant, REFERENTIEL_L53.inactiviteChronoMs);
          const saisie = globalThis.prompt?.('Nouveau temps passé, en minutes', String(Math.round(actuel / 60_000)));
          const minutes = Number(saisie);
          if (saisie === null || saisie === undefined || saisie.trim() === '' || Number.isNaN(minutes) || minutes < 0) return;
          const motif = (globalThis.prompt?.('Motif de la correction') ?? '').trim();
          if (motif !== '') void corrigerTemps(Math.round(minutes * 60_000), motif);
        }}
        onModifierEtapes={() => agirEditeur({ type: 'ouvrir' })}
        etapeChoisie={etapeChoisie}
        onChoisirEtape={setEtapeChoisie}
        journal={journal}
        courrierAnnonce={courrierAnnonce}
        lectriceDirigeante={lectriceDirigeante}
        onReessayerJournal={() => void chargerJournal()}
      />
      <EditeurEtapes
        etapes={dossier.steps}
        etat={editeur}
        onMonter={(id) => deplacerEtape(id, -1)}
        onDescendre={(id) => deplacerEtape(id, 1)}
        onDemanderSaut={(id) => agirEditeur({ type: 'demander-saut', id })}
        onMotif={(valeur) => agirEditeur({ type: 'motif', valeur })}
        onConfirmerSaut={() => {
          if (editeur.sautId === null) return;
          const id = editeur.sautId;
          const motif = editeur.motif;
          agirEditeur({ type: 'valider-saut' });
          void ecrire((a) => a.sauter(id, motif));
        }}
        onAnnulerSaut={() => agirEditeur({ type: 'annuler-saut' })}
        onAjouter={(phase, libelle, apres) => void ecrire((a) => a.ajouter(phase, libelle, apres))}
        onFermer={() => agirEditeur({ type: 'fermer' })}
      />
    </>
  );
}
